import React, { useState, useEffect } from 'react';
import { getAdminToken, setAdminToken, clearAdminToken } from '../utils/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_FR = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun',
  '07': 'Jul', '08': 'Aou', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
};

const AdminLoginPage = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok && data.success) { setAdminToken(data.token); onLogin(data.token); }
      else { setError(data.detail || 'Mot de passe incorrect'); }
    } catch { setError('Erreur de connexion au serveur'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1E3A5F', marginBottom: '10px', textAlign: 'center' }}>Administration</h1>
        <p style={{ color: '#64748B', fontSize: '14px', textAlign: 'center', marginBottom: '30px' }}>Atlantique Spa Boutique</p>
        <form onSubmit={handleSubmit} autoComplete="on">
          <input type="text" name="username" autoComplete="username" value="admin" readOnly
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} tabIndex={-1} />
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px', fontWeight: '500' }}>Mot de passe</label>
            <input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
              placeholder="Entrez le mot de passe" autoFocus />
          </div>
          {error && <div data-testid="login-error" style={{ padding: '12px 16px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '14px', marginBottom: '15px' }}>{error}</div>}
          <button type="submit" disabled={loading} data-testid="login-submit-btn"
            style={{ width: '100%', padding: '14px', backgroundColor: loading ? '#94A3B8' : '#1E3A5F', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminNav = ({ active }) => (
  <div style={{ display: 'flex', marginBottom: '24px', backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
    {[
      { key: 'dashboard', label: 'Dashboard', href: '/admin/dashboard' },
      { key: 'commandes', label: 'Commandes', href: '/admin/commandes' },
      { key: 'clients', label: 'Clients', href: '/admin/clients' },
      { key: 'factures', label: 'Factures', href: '/admin/factures' }
    ].map((tab, i) => (
      <a key={tab.key} href={tab.href} data-testid={`nav-${tab.key}`}
        style={{
          flex: 1, padding: '14px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600',
          color: active === tab.key ? 'white' : '#475569',
          backgroundColor: active === tab.key ? '#1E3A5F' : 'white',
          textDecoration: 'none',
          borderRight: i < 3 ? '1px solid #E2E8F0' : 'none'
        }}>
        {tab.label}
      </a>
    ))}
  </div>
);

const formatPrice = (price) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price || 0);
const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminDashboardPage = () => {
  const [token, setToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [recapMsg, setRecapMsg] = useState(null);

  useEffect(() => {
    const verify = async () => {
      const t = getAdminToken();
      if (!t) { setIsVerifying(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/admin/verify`, { headers: { 'Authorization': `Bearer ${t}` } });
        const d = await res.json();
        if (d.valid) setToken(t); else clearAdminToken();
      } catch { clearAdminToken(); }
      setIsVerifying(false);
    };
    verify();
  }, []);

  useEffect(() => {
    if (token) fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status === 401) { handleLogout(); return; }
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('Dashboard error:', e); }
    finally { setLoading(false); }
  };

  const handleLogin = (t) => setToken(t);
  const handleLogout = () => { clearAdminToken(); setToken(null); setData(null); };

  const handleSendRecap = async () => {
    setSendingRecap(true); setRecapMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-weekly-recap`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) setRecapMsg({ type: 'success', text: 'Recap hebdomadaire envoye a contact@atlantiquespaboutique.com' });
      else setRecapMsg({ type: 'error', text: d.detail || 'Erreur' });
    } catch { setRecapMsg({ type: 'error', text: 'Erreur de connexion' }); }
    finally { setSendingRecap(false); }
  };

  if (isVerifying) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}><p style={{ color: '#64748B' }}>Verification...</p></div>;
  if (!token) return <AdminLoginPage onLogin={handleLogin} />;

  const getStatusBadge = (status) => {
    const styles = { paid: { bg: '#FEF3C7', color: '#92400E', text: 'Payee' }, shipped: { bg: '#D1FAE5', color: '#065F46', text: 'Expediee' }, delivered: { bg: '#E0E7FF', color: '#3730A3', text: 'Livree' } };
    const s = styles[status] || { bg: '#F1F5F9', color: '#475569', text: status };
    return <span style={{ display: 'inline-block', padding: '3px 10px', backgroundColor: s.bg, color: s.color, borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{s.text}</span>;
  };

  // Find max CA for bar chart scaling
  const maxCA = data ? Math.max(...(data.evolution_ca || []).map(e => e.ca), 1) : 1;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F1F5F9', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <AdminNav active="dashboard" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Dashboard</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSendRecap} disabled={sendingRecap} data-testid="send-recap-btn"
              style={{ padding: '10px 18px', backgroundColor: '#6366F1', color: 'white', border: 'none', borderRadius: '8px', cursor: sendingRecap ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
              {sendingRecap ? 'Envoi...' : 'Envoyer recap'}
            </button>
            <button onClick={handleLogout} data-testid="logout-btn"
              style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748B' }}>
              Deconnexion
            </button>
          </div>
        </div>

        {recapMsg && (
          <div style={{ padding: '12px 18px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
            backgroundColor: recapMsg.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            color: recapMsg.type === 'success' ? '#065F46' : '#991B1B' }}>
            {recapMsg.text}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>Chargement du dashboard...</div>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
              {[
                { label: 'CA Total', value: formatPrice(data.ca_total), color: '#1E3A5F', icon: 'E' },
                { label: 'CA du mois', value: formatPrice(data.ca_mois), color: '#10B981', icon: 'M' },
                { label: 'Commandes (total)', value: data.nb_commandes_total, color: '#6366F1', icon: '#' },
                { label: 'Commandes (mois)', value: data.nb_commandes_mois, color: '#F59E0B', icon: 'C' },
                { label: 'Panier moyen', value: formatPrice(data.panier_moyen), color: '#EC4899', icon: 'P' },
              ].map((kpi, i) => (
                <div key={i} data-testid={`kpi-${i}`} style={{
                  backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.04)', borderLeft: `4px solid ${kpi.color}`
                }}>
                  <p style={{ color: '#64748B', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '500' }}>{kpi.label}</p>
                  <p style={{ color: '#0F172A', fontSize: '26px', fontWeight: '700', margin: 0 }}>{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Two columns: Evolution CA + Top 5 clients */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
              
              {/* CA Evolution Chart */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '20px' }}>Evolution du CA</h3>
                {(data.evolution_ca || []).length === 0 ? (
                  <p style={{ color: '#64748B', fontSize: '14px' }}>Aucune donnee</p>
                ) : (
                  <div data-testid="ca-evolution-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '180px' }}>
                    {(data.evolution_ca || []).map((item, i) => {
                      const pct = (item.ca / maxCA) * 100;
                      const parts = item.mois.split('-');
                      const label = MONTHS_FR[parts[1]] || parts[1];
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                          <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '600', marginBottom: '4px' }}>{formatPrice(item.ca)}</span>
                          <div style={{
                            width: '100%', maxWidth: '60px', borderRadius: '6px 6px 0 0',
                            backgroundColor: '#1E3A5F', height: `${Math.max(pct, 5)}%`,
                            transition: 'height 0.5s ease'
                          }} />
                          <span style={{ fontSize: '11px', color: '#64748B', marginTop: '6px' }}>{label} {parts[0].slice(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Top 5 Clients */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '16px' }}>Top 5 clients</h3>
                {(data.top_clients || []).length === 0 ? (
                  <p style={{ color: '#64748B', fontSize: '14px' }}>Aucun client</p>
                ) : (
                  <div data-testid="top-clients">
                    {(data.top_clients || []).map((c, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 0', borderBottom: i < (data.top_clients || []).length - 1 ? '1px solid #F1F5F9' : 'none'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: i === 0 ? '#FEF3C7' : i === 1 ? '#F1F5F9' : i === 2 ? '#FED7AA' : '#F8FAFC',
                            color: i === 0 ? '#92400E' : i === 1 ? '#64748B' : i === 2 ? '#9A3412' : '#94A3B8',
                            fontSize: '12px', fontWeight: '700'
                          }}>{i + 1}</span>
                          <div>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#0F172A' }}>{c.prenom && c.nom ? `${c.prenom} ${c.nom}` : c.name || c.prenom || c.nom || 'N/A'}</p>
                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>{c.order_count || c.nombre_commandes || 0} commande{(c.order_count || c.nombre_commandes || 0) > 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{formatPrice(c.total_spent || c.total_depense || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Top Products */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', marginBottom: '28px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '16px' }}>Top produits vendus</h3>
              {(data.top_products || []).length === 0 ? (
                <p style={{ color: '#64748B', fontSize: '14px' }}>Aucune donnee</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="top-products-table">
                    <thead>
                      <tr style={{ backgroundColor: '#F8FAFC' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Produit</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Quantite vendue</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CA genere</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.top_products || []).map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ ...tdStyle, width: '40px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: '24px', height: '24px', borderRadius: '50%', fontSize: '11px', fontWeight: '700',
                              backgroundColor: i === 0 ? '#FEF3C7' : i === 1 ? '#F1F5F9' : i === 2 ? '#FED7AA' : '#F8FAFC',
                              color: i === 0 ? '#92400E' : i === 1 ? '#64748B' : i === 2 ? '#9A3412' : '#94A3B8'
                            }}>{i + 1}</span>
                          </td>
                          <td style={tdStyle}><span style={{ fontWeight: '500', color: '#0F172A' }}>{p.name}</span></td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{ display: 'inline-block', padding: '3px 10px', backgroundColor: '#EFF6FF', color: '#1E40AF', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                              {p.total_sold}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}><span style={{ fontWeight: '600', color: '#0F172A' }}>{formatPrice(p.total_revenue)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Orders */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '16px' }}>Dernieres commandes</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="recent-orders-table">
                  <thead>
                    <tr style={{ backgroundColor: '#F8FAFC' }}>
                      <th style={thStyle}>Commande</th>
                      <th style={thStyle}>Client</th>
                      <th style={thStyle}>Montant</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.dernieres_commandes || []).map((o, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={tdStyle}><span style={{ fontWeight: '600', color: '#0F172A', fontSize: '13px' }}>#{o.order_number}</span></td>
                        <td style={tdStyle}>
                          <p style={{ margin: 0, fontWeight: '500', color: '#0F172A', fontSize: '13px' }}>{o.customer_name || 'N/A'}</p>
                          <p style={{ margin: '2px 0 0', color: '#64748B', fontSize: '12px' }}>{o.customer_email}</p>
                        </td>
                        <td style={tdStyle}><span style={{ fontWeight: '600', color: '#0F172A' }}>{formatPrice(o.total_amount)}</span></td>
                        <td style={{ ...tdStyle, color: '#64748B', fontSize: '13px' }}>{formatDate(o.created_at)}</td>
                        <td style={tdStyle}>{getStatusBadge(o.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>Erreur de chargement</div>
        )}
      </div>
    </div>
  );
};

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase' };
const tdStyle = { padding: '14px 16px', fontSize: '14px' };

export default AdminDashboardPage;
